import React, { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUtensils, FaTimes } from 'react-icons/fa';

const StickyIngredientsSidebarMobile = ({ 
  isVisible, 
  isOpen, 
  onOpen, 
  onClose, 
  ingredients, 
  processIngredients, 
  replaceLinkPlaceholder,
  isNewsletterModalOpen = false,
  isImageModalOpen = false
}) => {
  // MOBILE ONLY - check if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  
  // Store scroll position
  const scrollPositionRef = useRef(0);
  
  const { groups, hasGroups, normalized } = processIngredients(ingredients);
  
  // CRITICAL: Process links ONCE using useMemo to prevent multiple processing on re-renders
  const processedGroups = useMemo(() => {
    if (!hasGroups) return null;
    return groups.map(group => ({
      ...group,
      items: group.items.map(ing => replaceLinkPlaceholder(ing))
    }));
  }, [groups, hasGroups, replaceLinkPlaceholder]);

  const processedNormalized = useMemo(() => {
    if (!normalized) return [];
    return normalized.map(ing => replaceLinkPlaceholder(ing));
  }, [normalized, replaceLinkPlaceholder]);

  // Prevent body scroll when sidebar is open and preserve scroll position
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      // Apply styles to prevent scroll while maintaining visual position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position with proper timing
      const scrollY = scrollPositionRef.current;
      
      // Remove fixed positioning
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      // Use requestAnimationFrame to ensure scroll happens after styles are removed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollY,
            left: 0,
            behavior: 'auto'
          });
        });
      });
    }
    
    return () => {
      // Cleanup: restore scroll position if component unmounts while open
      if (isOpen) {
        const scrollY = scrollPositionRef.current;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollY,
              left: 0,
              behavior: 'auto'
            });
          });
        });
      }
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!ingredients) {
    return null;
  }

  // MOBILE ONLY - return null on desktop
  if (!isMobile) {
    return null;
  }

  if ((!hasGroups && (!normalized || normalized.length === 0)) || 
      (hasGroups && (!groups || groups.length === 0))) {
    return null;
  }

  const content = (
    <AnimatePresence>
      {/* Mobile Bottom Sheet */}
      {isOpen && !isImageModalOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop-mobile"
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm ${isNewsletterModalOpen ? 'z-[4999]' : isImageModalOpen ? 'z-[39]' : 'z-[9999]'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          />
          
          {/* Bottom Sheet */}
          <motion.div
            key="sticky-sidebar-mobile"
            className={`fixed inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl ${isNewsletterModalOpen ? 'z-[5000]' : isImageModalOpen ? 'z-[40]' : 'z-[10000]'}`}
            style={{ 
              maxHeight: '85vh',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-4 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <FaUtensils className="text-green-600 flex-shrink-0" />
                <h3 className="font-bold text-gray-800 font-['Playfair_Display'] text-lg">Składniki</h3>
              </div>
              <motion.button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/80 transition-colors text-gray-600 hover:text-gray-800 flex-shrink-0"
                aria-label="Zamknij"
                whileHover={{ scale: 1.15, rotate: 90 }}
                whileTap={{ scale: 0.85 }}
              >
                <FaTimes className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-4 pb-8 max-h-[calc(85vh-80px)] scrollbar-thin scrollbar-thumb-green-200 scrollbar-track-gray-100">
              {hasGroups ? (
                <div className="space-y-4">
                  {processedGroups.map((group, groupIdx) => (
                    <motion.div
                      key={groupIdx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: groupIdx * 0.1 }}
                      className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-lg p-4 border border-gray-200"
                    >
                      {group.title && (
                        <h4 className="text-base font-bold text-gray-800 mb-3 font-['Playfair_Display'] pb-2 border-b border-gray-300">
                          {group.title}
                        </h4>
                      )}
                      <ul className="list-disc pl-5 space-y-2 text-gray-800 text-sm sm:text-base">
                        {group.items.map((processedHtml, i) => (
                          <li key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: processedHtml }} />
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <ul className="list-disc pl-6 space-y-2 text-gray-800 text-sm sm:text-base">
                  {processedNormalized.map((processedHtml, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: processedHtml }}
                    />
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }
  
  return content;
};

export default StickyIngredientsSidebarMobile;

